import { countedWays, DISPLAY_UNITS, type Dimension, formatMoney, type UnitId } from "@bl/domain";
import { Link } from "@tanstack/react-router";
import { Check, PackagePlus, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  type AllocationMethod,
  type Invoice,
  type InvoiceLine,
  send,
  useChange,
  useItems,
} from "../api";
import { ErrorNote } from "../components/ui";
import { itemChoice, showDate } from "../format";
import { AddItemDialog, EditItemDialog } from "../forms/ItemDialogs";
import type { LineView } from "./InvoiceLotsStep";

/** How far the table must travel before the first column has fully stood down. */
const COLLAPSE_OVER = 120;
/**
 * A table that has been pushed sideways, so its first column can shrink to an anchor.
 *
 * On a phone the line table is six columns wide and the item column takes most of them. Keeping it
 * whole while the rest scrolls past leaves no room; dropping it entirely leaves numbers attached to
 * nothing. So it stays put and collapses: full detail at rest, the supplier's own code once the
 * brewer has scrolled off it (Tyler, 2026-09-22).
 */
function useScrolledSideways() {
  const ref = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [collapsing, setCollapsing] = useState(false);
  useEffect(() => {
    const wrap = ref.current;
    if (!wrap) return;
    let queued = 0;
    const measure = () => {
      queued = 0;
      // Nought to one across the first COLLAPSE_OVER pixels, so the column follows the finger both
      // ways rather than flipping at a threshold (Tyler, 2026-09-22). Everything it drives is a
      // paint — opacity and a width the browser eases — so this is a custom property, not state.
      const progress = Math.min(1, Math.max(0, wrap.scrollLeft / COLLAPSE_OVER));
      wrap.style.setProperty("--collapsed", progress.toFixed(3));
      // The detail leaves the flow the moment the anchor is opaque over it, rather than at the end
      // of the travel. Held to the end, a row stayed at its full height with nothing to show in it.
      setScrolled(progress >= 0.4);
      // Clipping only once it has started to move. At rest a long name is better spilling into the
      // empty column beside it, as it always did, than cut off inside its own.
      setCollapsing(progress > 0);
    };
    const onScroll = () => {
      // One update a frame: a table relayout per scroll event is more than a phone wants to do.
      if (!queued) queued = requestAnimationFrame(measure);
    };
    measure();
    wrap.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      wrap.removeEventListener("scroll", onScroll);
      if (queued) cancelAnimationFrame(queued);
    };
  }, []);
  /** Back to the start, which is where the item column and everything in it lives. */
  const home = () => ref.current?.scrollTo({ left: 0, behavior: "smooth" });
  return { ref, scrolled, collapsing, home };
}
/**
 * What to call a line when there is only room for a few characters. The supplier's own code where
 * they print one; YCH print none at all, so what the line was matched to, and failing that what the
 * supplier called it. Something is always better than a number attached to nothing.
 */
function anchorOf(line: LineView): string {
  return line.supplierSku || line.itemName || line.description;
}
export const METHODS: { value: AllocationMethod; label: string; hint: string }[] = [
  { value: "unit_count", label: "Per item", hint: "Every bag and carton takes the same share" },
  { value: "weight", label: "By weight", hint: "Heavier lines carry more of the freight" },
  { value: "extended_price", label: "By price", hint: "Costlier lines carry more of the freight" },
];
export const money = (value: string | null) => (value === null ? "" : formatMoney(value));
export function DocumentStep({ invoice, readOnly }: { invoice: Invoice; readOnly: boolean }) {
  const change = useChange(send.patchInvoice);
  const lines = useScrolledSideways();
  const stockLines = invoice.lines.filter((l) => l.receivesStock);

  return (
    <>
      <section className="section">
        <div className="section-header" style={{ cursor: "default" }}>
          <h2>As printed</h2>
        </div>
        <div className="section-body">
          <dl className="invoice-header">
            <Fact label="Invoice no." value={invoice.invoiceNumber} />
            <Fact label="Order no." value={invoice.orderNumber} />
            <Fact label="Order date" value={showDate(invoice.orderDate)} />
            <Fact label="Ship date" value={showDate(invoice.shipDate)} />
            <Fact label="Ship via" value={invoice.shipVia} />
            <Fact label="From" value={invoice.supplierName} />
            <Fact label="Your ref" value={invoice.customerRef} />
            <Fact label="Terms" value={invoice.paymentTerms} />
          </dl>
        </div>
      </section>

      {invoice.discrepancies.length > 0 ? (
        <div className="notice warn" role="status">
          <TriangleAlert size={16} />
          <div>
            <strong>The document does not add up to its own lines.</strong>
            <ul>
              {invoice.discrepancies.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
            <span className="muted">Left exactly as printed. Worth a word with the supplier.</span>
          </div>
        </div>
      ) : null}

      <section className="section">
        <div className="section-header" style={{ cursor: "default" }}>
          <h2>Line items</h2>
          <span className="aside">{invoice.lines.length} lines</span>
        </div>
        <div className="section-body">
          <div
            className={`table-wrap ${lines.collapsing ? "collapsing" : ""} ${
              lines.scrolled ? "pushed" : ""
            }`}
            ref={lines.ref}
          >
            <table className="table invoice-lines" aria-label="Line items">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Qty</th>
                  <th>Lot</th>
                  <th className="num">Unit price</th>
                  <th className="num">Total</th>
                  <th className="num">Freight &amp; fees</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lines.map((line) => (
                  <LineRow
                    key={line.id}
                    invoice={invoice}
                    line={line}
                    readOnly={readOnly}
                    onMatch={lines.home}
                  />
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">
                    {stockLines.length} of {invoice.lines.length} lines deliver stock
                  </th>
                  <td className="num">{invoice.computed.unitCount}</td>
                  <td />
                  <td />
                  <td className="num">{money(invoice.computed.goods)}</td>
                  <td className="num">{money(invoice.computed.charges)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </section>

      {invoice.charges.length > 0 ? (
        <section className="section">
          <div className="section-header" style={{ cursor: "default" }}>
            <h2>Freight, fees and taxes</h2>
          </div>
          <div className="section-body stack">
            <div className="table-wrap">
              <table className="table" aria-label="Freight, fees and taxes">
                <tbody>
                  {invoice.charges.map((c) => (
                    <tr key={c.id}>
                      <td>{c.label}</td>
                      <td className="num">
                        {c.percent !== null ? `${c.percent}%` : money(c.amount)}
                      </td>
                      <td className="num">{c.taxAmount ? `tax ${money(c.taxAmount)}` : ""}</td>
                      <td className="num">{money(c.effect)}</td>
                    </tr>
                  ))}
                  <tr>
                    <th scope="row">Printed total</th>
                    <td />
                    <td />
                    <td className="num">{money(invoice.printed.total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <fieldset className="method-picker">
              <legend>How these land in what the stock cost</legend>
              <p className="muted">
                The invoice reads the same either way. This only decides what each line carries.
              </p>
              <div className="segmented">
                {METHODS.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    aria-pressed={invoice.allocationMethod === m.value}
                    disabled={readOnly || change.isPending}
                    title={m.hint}
                    onClick={() =>
                      change.mutate({ id: invoice.id, patch: { allocationMethod: m.value } })
                    }
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <span className="hint">
                {METHODS.find((m) => m.value === invoice.allocationMethod)?.hint}
              </span>
              {invoice.allocationNote ? (
                <div className="notice warn" role="status">
                  <TriangleAlert size={16} />
                  <div>{invoice.allocationNote}</div>
                </div>
              ) : null}
              <ErrorNote error={change.error} />
            </fieldset>
          </div>
        </section>
      ) : null}

      {/* A weight that does not add up is nearly always the pallet and the packaging, so it is said
          last and calmly (Tyler and Scott, 2026-10-01). */}
      {invoice.weightDifference ? (
        <div className="notice warn" role="status">
          <TriangleAlert size={16} />
          <div>
            {invoice.weightDifference}.{" "}
            <span className="muted">
              Left as printed. Likely additional packaging/pallet weight.
            </span>
          </div>
        </div>
      ) : null}
    </>
  );
}
function Fact({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
/** How a line's catalog product is counted when added from it: as the invoice sells it, if it can be. */
function countedAsSold(line: InvoiceLine): Dimension | undefined {
  const category = line.catalogProduct?.category;
  if (!line.soldBy || !category) return undefined;
  return countedWays(category).includes(line.soldBy) ? line.soldBy : undefined;
}
/** The unit a line's catalog product is shown in when added from it: the one the invoice prints. */
function shownAsSold(line: InvoiceLine): UnitId | undefined {
  const dimension = countedAsSold(line);
  return dimension && line.soldIn && DISPLAY_UNITS[dimension].includes(line.soldIn)
    ? line.soldIn
    : undefined;
}
/** Giving a line's item a pack, in place, so the line can be received. */
export function GivePack({ itemId }: { itemId: string | null }) {
  const { data: items = [] } = useItems();
  const [open, setOpen] = useState(false);
  const item = items.find((i) => i.id === itemId);
  if (!item) return null;
  return (
    <>
      <button type="button" className="link-button" onClick={() => setOpen(true)}>
        Give it a pack
      </button>
      <EditItemDialog open={open} onClose={() => setOpen(false)} item={item} />
    </>
  );
}
function LineRow({
  invoice,
  line,
  readOnly,
  onMatch,
}: {
  invoice: Invoice;
  line: InvoiceLine;
  readOnly: boolean;
  onMatch: () => void;
}) {
  const { data: items = [] } = useItems();
  const change = useChange(send.patchInvoice);
  const matchTo = (itemId: string) =>
    change.mutate({ id: invoice.id, patch: { lines: [{ id: line.id, itemId }] } });
  // Adding the catalog's product and matching this line to it, in one press.
  const adopt = useChange(send.item, { onSuccess: (item) => matchTo(item.id) });
  // Or linking the brewery's own item made by hand to it, and matching the line to that.
  const claim = useChange(
    async (input: { itemId: string; catalogProductId: string }, key) => {
      await send.linkItem({ id: input.itemId, catalogProductId: input.catalogProductId }, key);
      return input.itemId;
    },
    { onSuccess: (itemId) => matchTo(itemId) },
  );
  const own = line.catalogProduct?.ownItem ?? null;
  const [adding, setAdding] = useState(false);
  const short = line.receivedUnits !== null && line.receivedUnits !== line.billedUnits;

  return (
    <tr className={line.receivesStock ? "" : "faint"}>
      <td>
        <span className="line-anchor" aria-hidden>
          {line.itemId || !line.receivesStock ? (
            anchorOf(line)
          ) : (
            // Nothing is matched yet, so the useful thing to say in 104px is what to do about it.
            // The controls are back at the start of the table, which is where this goes.
            <button type="button" className="button small primary anchor-match" onClick={onMatch}>
              Match
            </button>
          )}
        </span>
        <div className="line-item">
          {line.itemId ? (
            <span className="row line-matched">
              <span className="chip positive">
                <Check size={14} /> In inventory
              </span>
              {/* A new tab: following a link out of a half-reviewed invoice loses the brewer's place. */}
              <Link
                to="/items/$itemId"
                params={{ itemId: line.itemId }}
                target="_blank"
                rel="noreferrer"
              >
                {line.itemName}
              </Link>
            </span>
          ) : (
            <span className="chip new-item">
              <PackagePlus size={14} /> New
            </span>
          )}
          <span className="muted line-sku">
            {line.supplierSku ? `${line.supplierSku} · ` : ""}
            {line.description}
          </span>
          {line.needsPack && !readOnly ? (
            <span className="row line-needs-pack">
              <span className="chip danger">
                <TriangleAlert size={14} /> No pack size
              </span>
              <GivePack itemId={line.itemId} />
            </span>
          ) : null}
          {!line.itemId && !readOnly ? (
            <div className="stack line-match">
              {line.catalogProduct && own ? (
                // An item made by hand that is probably this product: Ollie's "Thomas Fawcett
                // Torrified Wheat" for TF-TWHT. Adding the product beside it made a second item that
                // the recipe and brew day never looked at (Tyler, 2026-10-19).
                <div className="row">
                  <button
                    type="button"
                    className="button primary"
                    disabled={claim.isPending}
                    onClick={() =>
                      claim.mutate({
                        itemId: own.id,
                        catalogProductId: line.catalogProduct?.id as string,
                      })
                    }
                  >
                    <Check size={14} /> Match to {own.name}
                  </button>
                  <span className="muted line-sku">Probably yours, under another name</span>
                </div>
              ) : null}
              {line.catalogProduct ? (
                <div className="row">
                  <button
                    type="button"
                    className={
                      own
                        ? "button"
                        : line.catalogProduct.via === "sku"
                          ? "button primary"
                          : "button suggested"
                    }
                    disabled={adopt.isPending}
                    onClick={() =>
                      adopt.mutate({
                        source: "catalog",
                        catalogProductId: line.catalogProduct?.id as string,
                        // Its own number from this supplier, and so a hop's brand, not the first the
                        // catalog lists.
                        supplierId: invoice.supplierId ?? undefined,
                        supplierSku: line.supplierSku ?? undefined,
                        // Counted the way the invoice sells it, where the product can be: a fining
                        // printed "1GAL" by the gallon.
                        dimension: countedAsSold(line),
                        // And shown in the unit it is sold in: a 250 mL bottle in mL.
                        displayUnit: shownAsSold(line),
                      })
                    }
                  >
                    <PackagePlus size={14} /> Add {line.catalogProduct.name}
                  </button>
                  {/* Short enough for the item column on a phone, which cannot wrap: every cell in
                      the table is nowrap, and this ran out across the quantity beside it (Tyler,
                      2026-09-23). The SKU is printed on the line above, so it is not said twice. */}
                  <span className="muted line-sku">
                    {own
                      ? "Only if yours is something else"
                      : line.catalogProduct.via === "sku"
                        ? "In the catalog, not stocked here yet"
                        : "Suggested from its wording — check it"}
                  </span>
                </div>
              ) : null}
              <div className="row">
                <select
                  className="select"
                  aria-label={`Match ${line.description}`}
                  value=""
                  disabled={change.isPending}
                  onChange={(e) => matchTo(e.target.value)}
                >
                  <option value="">Match to an item</option>
                  {items.map((i) => (
                    <option key={i.id} value={i.id}>
                      {itemChoice(i)}
                    </option>
                  ))}
                </select>
                <button type="button" className="button" onClick={() => setAdding(true)}>
                  {line.catalogProduct ? "Something else" : "Add it"}
                </button>
              </div>
              <ErrorNote error={adopt.error ?? claim.error} />
              <AddItemDialog
                open={adding}
                onClose={() => setAdding(false)}
                onAdded={(item) => matchTo(item.id)}
                // The SKU, because the catalog is searchable by it and a supplier's own wording is
                // not: "Great Western Malting Dextra Pils Malt" matches nothing, GW-DEXT matches it.
                initialQuery={line.supplierSku ?? line.description}
                submitLabel="Add to Inventory"
                offerInvoice={false}
                soldBy={line.soldBy}
                soldIn={line.soldIn}
              />
            </div>
          ) : null}
        </div>
      </td>
      <td className="num">
        {short ? (
          <>
            <s className="muted">{line.billedUnits}</s> {line.receivedUnits}
          </>
        ) : (
          line.billedUnits
        )}
        {(line.packLabel ?? line.printedPack) ? (
          <div className="muted line-sku">{line.packLabel ?? line.printedPack}</div>
        ) : null}
      </td>
      <td>
        {line.lots.length === 0 ? (
          <span className="muted">none printed</span>
        ) : (
          line.lots.map((l) => (
            <div key={l.id}>
              {l.lotCode}
              {l.cropYear ? <span className="muted"> {l.cropYear}</span> : null}
            </div>
          ))
        )}
      </td>
      <td className="num">{money(line.unitPrice)}</td>
      <td className="num">{money(line.extendedPrice)}</td>
      <td className="num">{money(line.allocatedCharge)}</td>
    </tr>
  );
}
